import { useEffect, useState } from 'react';
import { isNil, keyBy, xor } from 'lodash';
import GET_KEYBOARDING_MODULES_QUERY from 'graph/classroom/get-keyboarding-modules-query';
import { useQuery } from '@apollo/client';
import {
  KeyAssessmentAcceleratorScheduledDateInput,
  KeyboardingModule
} from 'types/schema-generated-types';
import { Identifiers } from './enums';
import isFuture from 'date-fns/isFuture';
import startOfDay from 'date-fns/startOfDay';
import { buildCheckedModuleMap, sanitizeFutureDates, sortByDate } from './utils';
import { AssessmentSubmodulesNames } from 'components/ReportsModule/types';

const modules = [
  {
    id: Identifiers.Module1,
    name: 'Module 1',
    submodule: AssessmentSubmodulesNames.MODULE_1_MATH,
    checked: false
  },
  {
    id: Identifiers.Module2,
    name: 'Module 2',
    submodule: AssessmentSubmodulesNames.MODULE_2_MATH_ELA,
    checked: false
  },
  {
    id: Identifiers.Module3,
    name: 'Module 3',
    submodule: AssessmentSubmodulesNames.MODULE_3_ELA,
    checked: false
  },
  {
    id: Identifiers.AllModules,
    name: 'All Modules',
    submodule: Object.values(AssessmentSubmodulesNames),
    checked: false
  }
];
type UseAssessmentScheduler = [
  {
    [dropdownRowIdx: number]: any;
    current: { dropdownRowIdx: number; moduleIdx: number };
    modules?: KeyboardingModule[];
    currentKeyboardingModules?: KeyboardingModule[];
    futureDates?: KeyAssessmentAcceleratorScheduledDateInput[];
    deletedModule?: boolean;
  },
  {
    addAssessmentModule: (currentModules: any, idx?: number) => void;
    getScheduledData: (datesList: any) => any[];
    updateModule: (dropdownRowIdx: number, moduleIdx: number) => void;
    deleteModule: (dropdownRowIdx: number) => void;
    deleteFutureModule: (data: any) => void;
    setFutureModule: (scheduledDates: any, reset?: boolean) => void;
    reset: () => void;
  }
];

const useAssessmentScheduler = (settingsValue: any): UseAssessmentScheduler => {
  const [state, setState] = useState<any>({});
  const [current, setCurrent] = useState<any>({ dropdownRowIdx: null, moduleIdx: null });
  const { data: modulesData } = useQuery<{ keyboardingModules: KeyboardingModule[] }>(
    GET_KEYBOARDING_MODULES_QUERY
  );

  const checkedResolver = (dropdownRowIdx: number, moduleIdx: any) => {
    const handleCheckAll = () => {
      const data = {
        ...state,
        [dropdownRowIdx]: {
          ...moduleMap,
          ...state?.[dropdownRowIdx],
          [moduleIdx]: {
            ...moduleMap[moduleIdx],
            checked: !moduleMap?.[moduleIdx]?.checked
          },
          ...keyBy(
            modules.map(x => ({ ...x, checked: !state?.[dropdownRowIdx]?.[moduleIdx]?.checked })),
            'id'
          )
        }
      };

      return data;
    };

    const moduleMap = keyBy(modules, 'id');
    if (moduleIdx === Identifiers.AllModules) {
      return handleCheckAll();
    }

    const nextState = {
      ...state,
      [dropdownRowIdx]: {
        ...moduleMap,
        ...state?.[dropdownRowIdx],
        [moduleIdx]: {
          ...moduleMap[moduleIdx],
          ...(state?.[dropdownRowIdx]?.[moduleIdx] ?? moduleMap[moduleIdx]),
          checked:
            !state?.[dropdownRowIdx]?.[moduleIdx]?.checked ?? !moduleMap[dropdownRowIdx].checked
        }
      }
    };

    return nextState;
  };

  const addAssessmentModule = (currentModules: any = null, idx: any) => {
    if (currentModules) {
      const current = {
        dropdownRowIdx: Object.keys(currentModules).length,
        moduleIdx: 0
      };

      setState({
        ...state,
        ...currentModules,
        current,
        ...(idx && {
          [Object.keys(currentModules).length]: keyBy(modules, 'id')
        })
      });
      return;
    }

    setState((prev: any) => ({
      ...prev,
      [idx]: keyBy(modules, 'id')
    }));
  };

  const reset = () => {
    setState({
      futureDates: state.futureDates,
      current: state.current,
      // eslint-disable-next-line
      [0]: keyBy(modules, 'id')
    });
  };

  const updateModule = (dropdownRowIdx: number, moduleIdx: number) => {
    setCurrent({ dropdownRowIdx, moduleIdx });
    setState(checkedResolver(dropdownRowIdx, moduleIdx));
  };

  const deleteModule = (dropdownRowIdx: number) => {
    setCurrent({ dropdownRowIdx: 0, moduleIdx: 0 });
    const nextState = { ...state };
    delete nextState[dropdownRowIdx];
    setState(nextState);
  };

  const deleteFutureModule = (data: any) => {
    const updatedFutureDates = state.futureDates.map((x: any) => {
      return {
        ...x,
        ...(x.date === data.date && {
          ...x,
          moduleIds: xor(x.moduleIds, data.moduleIds)
        })
      };
    });

    setState({ ...state, futureDates: updatedFutureDates, deletedModule: true });
  };

  const setFutureModule = (scheduledDates: any[], reset?: boolean) => {
    const futureDates = scheduledDates.map(s => ({ isFuture: isFuture(new Date(s.date)), ...s }));
    sortByDate(futureDates);

    // eslint-disable-next-line
    setState({ ...(reset ? { [0]: keyBy(modules, 'id') } : state), futureDates });
  };

  const getScheduledData = (scheduledDates: any[]) => {
    const data = scheduledDates.map((x, idx) => {
      return {
        date: x?.date ?? x,
        moduleIds: buildCheckedModuleMap(state[idx], modulesData)
          .map((x: any) => x.ids)
          .filter((x: any) => !isNil(x))
          .flat()
      };
    });
    const futureDates = sanitizeFutureDates(state.futureDates) ?? [];

    const newlyScheduled = data
      .map(x => {
        return { ...x, date: startOfDay(new Date(x.date)).toISOString() };
      })
      .filter(x => x.moduleIds.length);

    const result = [...(futureDates && futureDates), ...newlyScheduled];
    return result;
  };

  // load assessment state on mount

  useEffect(() => {
    addAssessmentModule(null, 0);
    // eslint-disable-next-line
  }, []);

  return [
    { ...state, current, modules, currentKeyboardingModules: modulesData?.keyboardingModules },
    {
      addAssessmentModule,
      updateModule,
      getScheduledData,
      deleteModule,
      deleteFutureModule,
      setFutureModule,
      reset
    }
  ];
};

export default useAssessmentScheduler;
